import { BehaviorSubject } from "rxjs";
import FormFillerService from "../../services/review-portal-services/form-filler.service";
import { mockForm } from "./mock.data";
import { myForms } from "./mock.myforms.data";
import ReviewPortalService from "../../services/review-portal-services/review-portal.service";
import { toast } from "react-toastify";
import { addAnswer, updateAnswer } from "./form.answer.fill";
import { validate } from "../Validations/form.validators";
import { currentPage } from "../views/MyForms";
import FormBuilderService from "../../services/form-builder.service";
import attachAnswersToForm from "../utilities/FormUtils";
import { computeGeIdTagging } from "../../utility/CommonUtils";

var currentFormId = undefined;
var expandedSectionId = undefined;
var currentSectionId = undefined;
var currentSubsectionId = undefined;
var searchParams = {};
var formErrors = {};
var currentSortParameter = {};
var rowsPerPageSize = 10;
const isMockMyFormFlow = false;
const isMockForm = false;

const parentDropDownId = new BehaviorSubject("");
const formState = new BehaviorSubject({});
const myFormsState = new BehaviorSubject([]);
const myFormsPageState = new BehaviorSubject();
const sitesState = new BehaviorSubject([]);
const sitesPageState = new BehaviorSubject();
const loaderState = new BehaviorSubject(false);
const openSectionState = new BehaviorSubject(expandedSectionId);
const currentSectionState = new BehaviorSubject(currentSectionId);
const currentSubsectionState = new BehaviorSubject(currentSubsectionId);
const formFillerService = new FormFillerService();
const reviewPortalService = new ReviewPortalService();
const formBuilderService = new FormBuilderService();
const formErrorState = new BehaviorSubject(formErrors);
const sortingSubject = new BehaviorSubject(currentSortParameter);
const turbinesState = new BehaviorSubject([]);
const turbinesPageState = new BehaviorSubject();
const QUESTION_COMMENTS_KEY = "notes";

const clone = (value) => JSON.parse(JSON.stringify(value || {}));

const syncCommentsInFormTree = (formTree, elementId, comments) => {
  const clonedTree = clone(formTree);
  let isUpdated = false;

  const updateElement = (element) => {
    if (!element || isUpdated) {
      return;
    }

    if ((element.elementId || element.id) === elementId) {
      element[QUESTION_COMMENTS_KEY] = comments;
      isUpdated = true;
      return;
    }

    if (Array.isArray(element.children)) {
      element.children.forEach(updateElement);
    }
  };

  if (Array.isArray(clonedTree.children)) {
    clonedTree.children.forEach(updateElement);
  }

  return clonedTree;
};

const syncCommentsInAnswerTree = (
  answerTree,
  elementId,
  comments,
  fallbackElement,
) => {
  const clonedTree = clone(answerTree);
  if (!Array.isArray(clonedTree.children)) {
    clonedTree.children = [];
  }

  let isUpdated = false;
  clonedTree.children = clonedTree.children.map((child) => {
    if ((child.elementId || child.id) === elementId) {
      isUpdated = true;
      return {
        ...child,
        [QUESTION_COMMENTS_KEY]: comments,
      };
    }
    return child;
  });

  if (!isUpdated && fallbackElement) {
    clonedTree.children.push({
      id: fallbackElement.id || fallbackElement.elementId,
      elementId: fallbackElement.elementId || fallbackElement.id,
      componentType: fallbackElement.componentType,
      answer: Array.isArray(fallbackElement.answer) ? fallbackElement.answer : [],
      [QUESTION_COMMENTS_KEY]: comments,
    });
  }

  return clonedTree;
};

class FormStore {
  static_instance = null;
  constructor() {
    this.form = {};
    this.updatedForm = {};
    this.initialAnswers = {};
    this.updatedAnswers = {};
    this.myForms = [];
    this.myFormsFilteredList = [];
    this.noSQLForm = false;
  }

  static sharedInstance() {
    if (FormStore._instance == null) {
      FormStore._instance = new FormStore();
    }
    return this._instance;
  }

  loadMyForms = () => {
    if (isMockMyFormFlow) {
      if (myForms) {
        this.myForms = myForms;
        this.didLoadMyForms(this.myForms);
      }
      return;
    } else {
      /*api call to be integrated for initial form list, for now considering empty list as initial state.*/
      this.didLoadMyForms([]);
    }
  };

  filterMyForms = (searchCriteria, rowsLength) => {
    searchParams = searchCriteria;
    rowsPerPageSize = rowsLength;
    if (isMockMyFormFlow) {
      if (myForms && myForms.content) {
        this.didLoadMyForms(myForms);
      }
      return;
    }
    this.onLoading(true);
    return reviewPortalService
      .getAllFormsBySortingorSearching(
        searchCriteria,
        currentSortParameter,
        currentPage,
        rowsPerPageSize,
      )
      .then((searchResults) => {
        this.didLoadMyForms(searchResults.data.content);
        this.didLoadMyFormsWithPagination(searchResults);
        return searchResults;
      })
      .catch((err) => {
        return err;
      });
  };

  didLoadMyForms(forms) {
    this.onLoading(false);
    this.myFormsFilteredList = forms;
    myFormsState.next(this.myFormsFilteredList);
  }

  didLoadMyFormsWithPagination(forms) {
    this.onLoading(false);
    myFormsPageState.next(forms);
  }

  onLoading = (isLoading) => {
    loaderState.next(isLoading);
  };

  load = async (formId) => {
    this.resetState();
    currentFormId = formId;
    await this.loadFormInternal(formId);
  };

  hydrateForm = async (formDataId) => {
    const { formTemplate, answerData } =
      await formFillerService.getForm(formDataId);
    const mergedForm = attachAnswersToForm(formTemplate, answerData);
    this.initializeForm(mergedForm, answerData);
  };

  loadFormInternal = async (formId) => {
    currentFormId = formId;
    this.onLoading(true);
    if (isMockForm) {
      const { answerData } = await formFillerService.getForm(formId);
      this.initializeForm(mockForm, answerData);
      return;
    }
    if (currentFormId === undefined) {
      console.log("Empty or Undefined formId");
    }
    try {
      await this.hydrateForm(formId);
    } catch (err) {
      console.error("Failed to load and merge form", err);
    } finally {
      this.onLoading(false);
    }
  };

  initializeForm = (formInfo, answerData) => {
    this.form = formInfo;
    this.updatedForm = JSON.parse(JSON.stringify(formInfo));
    this.initialAnswers = JSON.parse(JSON.stringify(answerData));
    this.updatedAnswers = JSON.parse(JSON.stringify(answerData));
    formState.next(formInfo);
    this.onLoading(false);
  };

  storeFormDataNoSQLID = (noSQLId) => {
    this.noSQLForm = noSQLId ? true : false;
  };

  getIsNoSQLForm = () => this.noSQLForm;

  openSection = (id) => {
    expandedSectionId = expandedSectionId !== id ? id : undefined;
    openSectionState.next(expandedSectionId);
  };

  currentSection = (id) => {
    // currentSectionId = (currentSectionId !== id) ? id : undefined;
    currentSectionId = id;
    currentSectionState.next(currentSectionId);
  };

  openSubSection = (id) => {
    currentSubsectionId = currentSubsectionId !== id ? id : undefined;
    currentSubsectionState.next(currentSubsectionId);
  };

  resetState = () => {
    formErrors = {};
    expandedSectionId = undefined;
    currentSectionId = undefined;
    currentSubsectionId = undefined;
    openSectionState.next(expandedSectionId);
    currentSectionState.next(currentSectionId);
    currentSubsectionState.next(currentSubsectionId);
    formErrorState.next(formErrors);
    this.updatedAnswers = this.initialAnswers;
  };

  parentDropDownId = (parentSelectedObject) => {
    parentDropDownId.next(parentSelectedObject);
  };

  deleteForm = (formId, enableIsButtonDisabled) => {
    this.onLoading(true);
    formFillerService.deleteForm(formId).then((res) => {
      this.onLoading(false);
      if (res.status !== 200) {
        toast.error("Could not delete form, " + res.message);
        return;
      }
      toast.success("Form deleted successfully!");
      this.filterMyForms(searchParams, rowsPerPageSize);
      enableIsButtonDisabled();
    });
  };

  approveForm = (formObject, enableIsButtonDisabled) => {
    this.onLoading(true);
    formFillerService.approveForm(formObject).then((res) => {
      this.onLoading(false);
      if (res.status !== 200) {
        toast.error("Could not approve form, " + res.message);
        return;
      }
      toast.success("Form Approved successfully!");
      this.filterMyForms(searchParams, rowsPerPageSize);
      this.load(res.data);
      enableIsButtonDisabled();
    });
  };

  rejectForm = (formObject, enableIsButtonDisabled) => {
    this.onLoading(true);
    formFillerService.rejectForm(formObject).then((res) => {
      this.onLoading(false);
      if (res.status !== 200) {
        toast.error("Could not reject form, " + res.message);
        return;
      }
      toast.success("Form Rejected successfully!");
      this.filterMyForms(searchParams, rowsPerPageSize);
      this.load(res.data);
      enableIsButtonDisabled();
    });
  };

  downloadForm = (form, enableIsButtonDisabled) => {
    this.onLoading(true);
    let warningShown = false;
    const warningTimer = setTimeout(() => {
      toast.warn(
        <div>
          Your request to download the form has been successfully submitted.
          <br />
          Please check your email for the PDF download link, which will be sent
          to you shortly.
        </div>,
        {
          containerId: "formstore-container",
          className: "formstore-toast",
          autoClose: 10000,
        },
      );
      warningShown = true;
    }, 25000);
    formFillerService.downloadForm(form.formDataId).then((res) => {
      clearTimeout(warningTimer);
      this.onLoading(false);
      if (res.status === 504) {
        if (!warningShown) {
          toast.warn(
            <div>
              Your request to download the form has been successfully submitted.
              <br />
              Please check your email for the PDF download link, which will be
              sent to you shortly.
            </div>,
            {
              containerId: "formstore-container",
              className: "formstore-toast",
              autoClose: 10000,
            },
          );
        }
        return;
      }
      if (res.status !== 200) {
        toast.error("Could not download form, " + res.message);
        return;
      }
      // Only show success if warning was NOT already shown
      if (!warningShown) {
        let blob = new Blob([res.data], { type: "application/pdf" });
        let downloadLink = document.createElement("a");
        let fileURL = URL.createObjectURL(blob);
        downloadLink.href = fileURL;
        downloadLink.download = form.formName + "_" + form.formDataId;
        downloadLink.click();
        URL.revokeObjectURL(fileURL);
        toast.success(
          <div>
            The form has been downloaded successfully.
            <br />
            Additionally, you will receive a PDF download link via email
            shortly.
          </div>,
          {
            containerId: "formstore-container",
            className: "formstore-toast",
            autoClose: 10000,
          },
        );
      }
      enableIsButtonDisabled();
    });
  };

  downloadSelectedForms = (formIds, enablePDFButton) => {
    if (formIds.length === 1) {
      const id = formIds[0];
      let warningShown = false;
      const warningTimer = setTimeout(() => {
        toast.warn(
          <div>
            Your request to download the form has been successfully submitted.
            <br />
            Please check your email for the PDF download link, which will be
            sent to you shortly.
          </div>,
          {
            containerId: "formstore-container",
            className: "formstore-toast",
            autoClose: 10000,
          },
        );
        warningShown = true;
      }, 25000);
      formFillerService.downloadForm(id).then((res) => {
        clearTimeout(warningTimer);
        this.onLoading(false);
        if (res.status === 504) {
          if (!warningShown) {
            toast.warn(
              <div>
                Your request to download the form has been successfully
                submitted.
                <br />
                Please check your email for the PDF download link, which will be
                sent to you shortly.
              </div>,
              {
                containerId: "formstore-container",
                className: "formstore-toast",
                autoClose: 10000,
              },
            );
          }
          enablePDFButton();
          return;
        }
        if (res.status !== 200) {
          toast.error("Could not download form, " + res.message);
          enablePDFButton();
          return;
        }
        if (!warningShown) {
          let blob = new Blob([res.data], { type: "application/pdf" });
          let downloadLink = document.createElement("a");
          let fileURL = URL.createObjectURL(blob);
          downloadLink.href = fileURL;
          downloadLink.download = "Receiving_Inspection_" + Date.now();
          downloadLink.click();
          URL.revokeObjectURL(fileURL);
          toast.success(
            <div>
              The form has been downloaded successfully.
              <br />
              Additionally, you will receive a PDF download link via email
              shortly.
            </div>,
            {
              containerId: "formstore-container",
              className: "formstore-toast",
              autoClose: 10000,
            },
          );
        }
        enablePDFButton();
      });
    } else {
      formFillerService.downloadBatchForms(formIds).then((res) => {
        this.onLoading(false);
        if (res.status < 200 || res.status >= 300) {
          toast.error("Could not process bulk PDF request, " + res.message);
          enablePDFButton();
          return;
        }
        const responseMessage =
          res?.data ||
          "Bulk PDF processing has been initiated successfully. You will receive pdf files download link via email shortly";
        toast.warn(responseMessage, {
          containerId: "formstore-container",
          className: "formstore-toast",
          autoClose: 10000,
        });
        enablePDFButton();
      }).catch(() => {
        toast.error("Could not process bulk PDF request.");
        enablePDFButton();
      });
    }
  };

  recordAnswer(answer, element) {
    let errors = validate(element, answer);
    this.updateError(element.id, errors);
    let updatedForm = addAnswer(
      answer,
      element.id || element.elementId,
      this.updatedForm,
    );
    if (updatedForm) {
      this.updatedForm = updatedForm;
      formState.next(updatedForm);
    }
    const updatedAnswer = updateAnswer(
      this.updatedAnswers,
      answer,
      element?.elementId,
    );
    if (updatedAnswer) {
      this.updatedAnswers = updatedAnswer;
    }
  }

  upsertQuestionComment = (element, commentText, currentUser = {}) => {
    const elementId = element?.elementId || element?.id;
    if (!elementId || !commentText?.trim()) {
      return;
    }

    const existingComments = Array.isArray(element?.notes)
      ? element.notes
      : [];

    const now = `${new Date().getTime()}`;
    const commentNode = {
      comment: commentText.trim(),
      lastUpdatedBy: currentUser?.sso || localStorage.getItem("ge_sso") || "",
      lastUpdatedDate: now,
      userFirstName: currentUser?.userFirstName || "",
      userLastName: currentUser?.userLastName || "",
    };

    const updatedComments = [...existingComments, commentNode];
    this.applyQuestionComments(elementId, updatedComments, element);
    this.saveQuestionComments();
  };

  editQuestionComment = (
    element,
    commentIndex,
    updatedComment,
    currentUser = {},
  ) => {
    const elementId = element?.elementId || element?.id;
    if (!elementId || commentIndex < 0 || !updatedComment?.trim()) {
      return;
    }

    const existingComments = Array.isArray(element?.notes)
      ? element.notes
      : [];
    const now = `${new Date().getTime()}`;
    const comments = existingComments.map((item, index) =>
      index === commentIndex
        ? {
            ...item,
            comment: updatedComment.trim(),
            lastUpdatedDate: now,
            lastUpdatedBy:
              currentUser?.sso || localStorage.getItem("ge_sso") || "",
            userFirstName: currentUser?.userFirstName || item.userFirstName || "",
            userLastName: currentUser?.userLastName || item.userLastName || "",
          }
        : item,
    );

    this.applyQuestionComments(elementId, comments, element);
    this.saveQuestionComments();
  };

  deleteQuestionComment = (element, commentIndex) => {
    const elementId = element?.elementId || element?.id;
    if (!elementId || commentIndex < 0) {
      return;
    }

    const existingComments = Array.isArray(element?.notes)
      ? element.notes
      : [];
    const comments = existingComments.filter((item, index) => index !== commentIndex);

    this.applyQuestionComments(elementId, comments, element);
    this.saveQuestionComments();
  };

  saveQuestionComments = () => {
    const payload = JSON.parse(JSON.stringify(this.updatedAnswers));
    if (!payload?.formDataId) {
      return;
    }
    formFillerService.saveForm(payload.formDataId, payload).then((res) => {
      if (res.status !== 200) {
        toast.error("Could not save comment, " + res.message);
      }
    });
  };

  applyQuestionComments = (elementId, comments, fallbackElement) => {
    this.updatedForm = syncCommentsInFormTree(this.updatedForm, elementId, comments);
    this.updatedAnswers = syncCommentsInAnswerTree(
      this.updatedAnswers,
      elementId,
      comments,
      fallbackElement,
    );
    formState.next(this.updatedForm);
  };

  updateError(elementId, errors) {
    if (elementId) {
      if (errors.length === 0) {
        delete formErrors[elementId];
      } else {
        formErrors[elementId] = errors;
      }
      formErrorState.next(formErrors);
    }
  }

  saveSiteTurbineDetails = (payload, enableIsButtonDisabled) => {
    const syncDependentDropdownFields = (formLikeObject) => {
      if (!formLikeObject || !Array.isArray(formLikeObject.children)) {
        return formLikeObject;
      }

      let hasSyncedDependentDropdown = false;
      const updatedChildren = formLikeObject.children.map((child) => {
        if (
          hasSyncedDependentDropdown ||
          child?.componentType !== "dependentDropdown" ||
          !Array.isArray(child.answer) ||
          child.answer.length === 0 ||
          typeof child.answer[0] !== "object" ||
          child.answer[0] === null
        ) {
          return child;
        }

        const syncedAnswer = {
          ...child.answer[0],
          regionId: payload.regionId || "",
          regionName: payload.regionName || "",
          siteId: payload.siteId || "",
          siteName: payload.siteName || "",
          turbineId: payload.turbineId || "",
          turbineName: payload.turbineName || "",
        };

        hasSyncedDependentDropdown = true;

        return {
          ...child,
          answer: [syncedAnswer, ...child.answer.slice(1)],
        };
      });

      return {
        ...formLikeObject,
        ...payload,
        children: updatedChildren,
      };
    };

    this.updatedForm = syncDependentDropdownFields(this.updatedForm);
    this.updatedAnswers = syncDependentDropdownFields(this.updatedAnswers);

      const result = this.saveForm(enableIsButtonDisabled);
      if (result && typeof result.then === 'function') {
        return result.then((res) => {
          this.filterMyForms(searchParams, rowsPerPageSize);
          return res;
        });
      } else {
        this.filterMyForms(searchParams, rowsPerPageSize);
        return Promise.resolve(result);
      }
  };

  resetForm = () => {
    this.resetState();
    formState.next(JSON.parse(JSON.stringify(this.form)));
  };

  saveForm = (enableIsButtonDisabled) => {
    this.onLoading(true);
    const payload = JSON.parse(JSON.stringify(this.updatedAnswers));
    const updatedGeIdTagValue = computeGeIdTagging(this.form, payload);
    if (
      payload.geIdTagDetails &&
      payload.geIdTagDetails.geIdTag !== updatedGeIdTagValue
    ) {
      payload.geIdTagDetails = {
        geIdTag: updatedGeIdTagValue,
        status: null,
        manufacturerName: null,
      };
    }
    return formFillerService
      .saveForm(payload.formDataId, payload)
      .then(async (res) => {
        if (res.status !== 200) {
          toast.error("Could not save form, " + res.message);
          this.onLoading(false);
          return Promise.reject(res);
        }
        this.resetState();
        toast.success("Form saved successfully!");
        const {
          data: { formDataId },
        } = res;
        this.hydrateForm(formDataId);
        this.onLoading(false);
        enableIsButtonDisabled();
        return res;
      });
  };

  sortForms = (sortParameter) => {
    currentSortParameter = sortParameter;
    this.sortFormsInternal(currentSortParameter);
  };

  onSortChanged = (sortParameter) => {
    sortingSubject.next(sortParameter);
  };

  sortFormsInternal = (sortParameter) => {
    if (isMockMyFormFlow) {
      if (myForms) FormStore.sharedInstance().didLoadMyForms(myForms);
      return;
    }
    this.onSortChanged(sortParameter);
    this.fireApi();
  };

  pageChanged = () => {
    if (isMockMyFormFlow) {
      if (myForms) FormStore.sharedInstance().didLoadMyForms(myForms);
      return;
    }
    this.fireApi();
  };

  getAllTurbinesBySiteId = (siteId) => {
    this.didLoadTurbines([]);
    formBuilderService.getAllTurbinesBySite(siteId).then((response) => {
      if (response.status === 200 || response.status === 204) {
        this.didLoadTurbines(response.data.turbines);
        this.didLoadTurbinesWithPagination(response);
      }
    });
  };

  didLoadTurbines = (turbines) => {
    turbinesState.next(turbines);
  };

  didLoadTurbinesWithPagination = (turbines) => {
    turbinesPageState.next(turbines);
  };

  fireApi = () => {
    // When search query is present
    if (Object.keys(searchParams).length !== 0) {
      // If search query is present, trigger search api. For >0 and <3 chars, Sorting will be on the results displayed from the last search
      reviewPortalService
        .getAllFormsBySortingorSearching(
          searchParams,
          currentSortParameter,
          currentPage,
          rowsPerPageSize,
        )
        .then((res) => {
          this.didLoadMyForms(res.data.content);
          this.didLoadMyFormsWithPagination(res);
        });
    }
  };

  getAllForms = (enableCSVButton) => {
    if (Object.keys(searchParams).length !== 0) {
      reviewPortalService.getAllFormsBySearching(searchParams).then((res) => {
        if (res.status !== 200) {
          toast.error("Could not download csv file, " + res.message);
          return;
        } else {
          const filename = "FieldWork_Review_FormData.csv";
          const blob = new Blob([res.data], {
            type: "text/csv;charset=utf-8;",
          });
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = filename;
          link.click();
          toast.success("CSV file downloaded successfully!");
        }
        enableCSVButton();
      });
    }
  };
}

export {
  FormStore,
  myFormsState,
  loaderState,
  formState,
  openSectionState,
  currentSectionState,
  currentSubsectionState,
  parentDropDownId,
  formErrorState,
  sortingSubject,
  myFormsPageState,
  sitesState,
  sitesPageState,
  turbinesState,
  turbinesPageState,
};
